import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import * as React from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { UserPlus } from 'lucide-react';
import { InviteForm } from '@/components/form/InviteForm';
import { useState } from 'react';
import { InviteTable } from '@/components/table/InviteTable';
import { ScheduleUserTable } from '@/components/table/ScheduleUserTable';
import { Label } from '@/components/ui/label';
import { useSchedules } from '@/hooks/swr/use-schedules';
import { useInvites } from '@/hooks/swr/use-invites';

// Custom hook to get schedule name by ID
const useScheduleName = (scheduleId: string) => {
  const { schedules } = useSchedules();
  const schedule = schedules.find((s) => s.id === scheduleId);
  return schedule?.name || '';
};

interface InviteDialogProps {
  scheduleId: string;
  trigger?: React.ReactNode;
}

export const InviteDialog = ({ scheduleId, trigger }: InviteDialogProps) => {
  const t = useTranslations('calendar');
  const [open, setOpen] = useState(false);
  const scheduleName = useScheduleName(scheduleId);
  const { invites } = useInvites({ scheduleId });

  const defaultTrigger = (
    <Button variant="outline" size="sm" className="invisible group-hover:visible">
      <UserPlus className="mr-1 h-4 w-4" />
      {t('invite')}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="flex max-h-[80vh] flex-col gap-0 p-0 sm:max-w-lg md:max-w-2xl">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="text-xl font-semibold">{t('members')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col overflow-hidden">
          {/* Invite Form Section - At the top */}
          <div className="space-y-4 border-b bg-gray-50/50 p-6">
            <div className="flex items-center gap-2">
              <Label className="text-gray-700">
                {t('inviteCollaborateText')} {scheduleName && ` ${scheduleName}`}.
              </Label>
            </div>
            <InviteForm scheduleId={scheduleId} />
          </div>

          {/* Content Area - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-6 p-6">
              {/* Pending Invitations Section - Only show if there are pending invites */}
              {invites.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium text-gray-900">
                      {t('tables.invite.title')}
                    </Label>
                  </div>
                  <div className="rounded-lg border bg-white">
                    <InviteTable scheduleId={scheduleId} />
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium text-gray-900">
                    {t('tables.scheduleUser.title')}
                  </Label>
                </div>
                <div className="rounded-lg border bg-white">
                  <ScheduleUserTable scheduleId={scheduleId} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
